/**
 * Welcome Page - Personalized onboarding for offered parents
 * 
 * URL: /welcome/:token
 * 
 * Flow:
 * 1. Validate welcome token
 * 2. Show prepopulated account creation form
 * 3. After account creation → Child selector (Netflix-style)
 * 4. Review pricing → Add payment info → Start trial
 * 
 * Updates CRM status to "in_trial" on completion
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  Eye,
  EyeOff,
  Plus,
  User,
  ChevronRight
} from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
import { glassStyles } from '@/lib/design';
import { useTranslation, toLanguageCode } from '@/lib/i18n';
import { LanguageSelector } from '@/components/shared/LanguageSelector';
import { resolvePackageDisplay, generateTrialTerms } from './welcomePricing';
import type { PackageDisplay } from './welcomePricing';
import { WelcomePricingStep } from './WelcomePricingStep';

interface TokenData {
  lead_id: string;
  contact_id: string;
  contact_first_name: string;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_phone_sms: string | null;
  contact_phone_whatsapp: string | null;
  contact_city: string | null;
  contact_preferred_language: string | null;
  child_first_name: string | null;
  child_birth_day: number | null;
  child_birth_month: number | null;
  child_birth_year: number | null;
  is_valid: boolean;
  error_message: string | null;
  assigned_package_name: string | null;
  assigned_package_price_cents: number | null;
  assigned_package_trial_days: number | null;
  assigned_package_billing_interval: string | null;
  assigned_package_stripe_price_id: string | null;
  assigned_package_class_rate_cents?: number | null;
}

interface ChildOption {
  id: string;
  name: string;
  isNew?: boolean;
}

type Step = 'loading' | 'error' | 'create_account' | 'select_child' | 'pricing' | 'success';

export const WelcomePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t, language, setLanguage: setContextLanguage } = useTranslation();
  
  const [step, setStep] = useState<Step>('loading');
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Account creation form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Child selection
  const [children, setChildren] = useState<ChildOption[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [addingChild, setAddingChild] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  
  // Pricing/Trial
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isExistingSubscriber, setIsExistingSubscriber] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [packageInfo, setPackageInfo] = useState<PackageDisplay | null>(null);
  
  // User session after account creation
  const [userId, setUserId] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const tokenValidatedRef = React.useRef(false);

  // Validate token on mount (only once — skip if already validated)
  useEffect(() => {
    if (!token) {
      setError('No token provided');
      setStep('error');
      return;
    }
    if (tokenValidatedRef.current) return;
    tokenValidatedRef.current = true;
    
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      // Custom RPC not in generated types - use type assertion
      const { data, error: rpcError } = await (supabase.rpc as any)(
        'validate_welcome_token', 
        { p_token: token }
      );
      
      if (rpcError) throw rpcError;
      
      const dataArray = data as TokenData[] | null;
      if (!dataArray || dataArray.length === 0) {
        setError('Invalid or expired welcome link');
        setStep('error');
        return;
      }
      
      const result = dataArray[0];
      
      if (!result.is_valid) {
        setError(result.error_message || 'This welcome link is no longer valid');
        setStep('error');
        return;
      }
      
      setTokenData(result);
      setEmail(result.contact_email || '');
      
      // Resolve assigned package (or fetch default)
      const assigned = resolvePackageDisplay(result);
      if (assigned) {
        // RPC doesn't return class_rate_cents yet — fetch from subscription_packages
        if (assigned.class_rate_cents == null && assigned.stripe_price_id) {
          const { data: pkg } = await supabase
            .from('subscription_packages')
            .select('class_rate_cents')
            .eq('stripe_price_id', assigned.stripe_price_id)
            .maybeSingle();
          assigned.class_rate_cents = pkg?.class_rate_cents ?? null;
        }
        setPackageInfo(assigned);
      } else {
        // Fallback: load default package from database
        const { data: defaultPkg } = await supabase
          .from('subscription_packages')
          .select('name, price_cents, trial_days, billing_interval, stripe_price_id, class_rate_cents')
          .eq('is_active', true)
          .eq('is_default', true)
          .maybeSingle();
        if (defaultPkg && defaultPkg.stripe_price_id) {
          setPackageInfo(defaultPkg as PackageDisplay);
        }
      }
      
      // Apply CRM language preference to translation context
      if (result.contact_preferred_language) {
        const lang = toLanguageCode(result.contact_preferred_language);
        if (lang !== 'en') {
          setContextLanguage(lang);
        }
      }
      
      // Pre-populate child from CRM data
      if (result.child_first_name) {
        setChildren([{ 
          id: 'crm_child', 
          name: result.child_first_name 
        }]);
        setSelectedChildId('crm_child');
      }
      
      // Check if user is already authenticated (retry/resume scenario)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (existingProfile) {
          setUserId(session.user.id);
          setProfileId(existingProfile.id);
          setEmail(session.user.email || result.contact_email || '');

          const { data: hm } = await supabase
            .from('household_members')
            .select('household_id')
            .eq('profile_id', existingProfile.id)
            .maybeSingle();

          let currentHouseholdId = hm?.household_id || null;

          // Existing user with no household — run setup_parent_account to create one.
          // This handles the case where a previous attempt failed partway through.
          if (!currentHouseholdId && result.contact_id) {
            try {
              const { data: setupResult } = await (supabase.rpc as any)(
                'setup_parent_account',
                {
                  p_profile_id: existingProfile.id,
                  p_name: result.contact_first_name,
                  p_email: session.user.email || result.contact_email || '',
                  p_family_name: `${result.contact_first_name} Family`,
                  p_phone_sms: result.contact_phone_sms || null,
                  p_phone_whatsapp: result.contact_phone_whatsapp || null,
                  p_city: result.contact_city || null,
                  p_contact_id: result.contact_id || null,
                  p_preferred_language: result.contact_preferred_language
                    ? toLanguageCode(result.contact_preferred_language)
                    : null,
                }
              );
              const hhId = Array.isArray(setupResult)
                ? setupResult[0]?.household_id
                : setupResult?.household_id;
              if (hhId) currentHouseholdId = hhId;
            } catch (setupErr) {
              console.error('Resume: setup_parent_account failed:', setupErr);
              Sentry.captureException(setupErr, { tags: { flow: 'welcome_resume', step: 'setup_parent_account' } });
            }
          }

          if (currentHouseholdId) setHouseholdId(currentHouseholdId);

          const { data: existingChildren } = await supabase
            .from('children')
            .select('id, name')
            .eq('parent_id', existingProfile.id);

          if (existingChildren && existingChildren.length > 0) {
            setChildren([{ id: existingChildren[0].id, name: existingChildren[0].name }]);
            setSelectedChildId(existingChildren[0].id);

            // Eagerly consume token + link CRM on resume (idempotent)
            if (token && existingProfile.id) {
              try {
                await (supabase.rpc as any)('use_welcome_token', {
                  p_token: token,
                  p_profile_id: existingProfile.id,
                  p_child_id: existingChildren[0].id,
                });
              } catch (tokenErr) {
                console.error('Resume: use_welcome_token failed:', tokenErr);
                Sentry.captureException(tokenErr, { tags: { flow: 'welcome_resume', step: 'use_welcome_token' } });
              }
            }

            setStep('pricing');
            return;
          }

          if (currentHouseholdId) {
            setStep('select_child');
            return;
          }
        }
      }

      setStep('create_account');
      
    } catch (err) {
      console.error('Token validation error:', err);
      Sentry.captureException(err, { tags: { flow: 'welcome', step: 'validate_token' } });
      setError('Failed to validate welcome link');
      setStep('error');
    }
  };

  const handleCreateAccount = async () => {
    if (!tokenData) return;
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setCreating(true);
    setError(null);
    
    try {
      // 1. Create auth user (or sign in if already registered)
      let authUserId: string;
      const trimmedEmail = email.trim().toLowerCase().replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        setError('Please enter a valid email address.');
        setStep('create_account');
        return;
      }
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          data: {
            name: `${tokenData.contact_first_name} ${tokenData.contact_last_name || ''}`.trim(),
          },
        },
      });
      
      if (authError) {
        if (authError.message?.includes('already registered') || authError.message?.includes('already exists')) {
          // Auto-sign in and resume setup — don't strand the user
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: trimmedEmail, password,
          });
          if (signInError) {
            setError('Account exists but sign-in failed. Please try logging in directly.');
            return;
          }
          if (!signInData.user) throw new Error('Sign-in returned no user');
          authUserId = signInData.user.id;
        } else {
          throw authError;
        }
      } else {
        if (!authData.user) throw new Error('Failed to create account');
        authUserId = authData.user.id;
      }
      
      setUserId(authUserId);
      
      // 2. Poll for profile creation (trigger creates it asynchronously)
      let profile = null;
      let attempts = 0;
      const maxAttempts = 8;
      let delay = 150;
      
      while (!profile && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, delay));
        
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', authUserId)
          .maybeSingle();
        
        if (profileData) {
          profile = profileData;
          break;
        }
        
        attempts++;
        delay = Math.min(delay * 2, 2000);
      }
      
      if (!profile) {
        throw new Error('Profile creation timed out. Please try again.');
      }
      
      // 3. Create household, link parent, and update profile (via SECURITY DEFINER RPC to bypass RLS)
      const { data: setupResult, error: setupError } = await (supabase.rpc as any)(
        'setup_parent_account',
        {
          p_profile_id: profile.id,
          p_name: tokenData.contact_first_name,
          p_email: trimmedEmail,
          p_family_name: `${tokenData.contact_first_name} Family`,
          p_phone_sms: tokenData.contact_phone_sms || null,
          p_phone_whatsapp: tokenData.contact_phone_whatsapp || null,
          p_city: tokenData.contact_city || null,
          p_contact_id: tokenData.contact_id || null,
          p_preferred_language: language !== 'en' ? language
            : tokenData.contact_preferred_language
              ? toLanguageCode(tokenData.contact_preferred_language)
              : null,
        }
      );

      if (setupError) {
        console.error('Setup parent account error:', setupError);
        throw new Error(setupError.message || 'Failed to set up account. Please try again.');
      }

      // Set last_name separately (RPC only sets name field)
      if (tokenData.contact_last_name) {
        await supabase
          .from('profiles')
          .update({ last_name: tokenData.contact_last_name })
          .eq('id', profile.id);
      }

      const householdIdResult = Array.isArray(setupResult) ? setupResult[0]?.household_id : setupResult?.household_id;
      if (!householdIdResult) {
        throw new Error('Account setup completed but household was not created. Please contact support.');
      }

      // Auto-invite secondary contacts from CRM into the new household
      if (tokenData.lead_id) {
        try {
          await supabase.functions.invoke('send-household-invite', {
            body: {
              household_id: householdIdResult,
              auto_invite_from_lead: tokenData.lead_id,
            },
          });
        } catch (inviteErr) {
          console.error('Non-fatal: failed to auto-invite secondary contacts:', inviteErr);
        }
      }

      setProfileId(profile.id);
      setHouseholdId(householdIdResult);

      // Check if child already exists from a previous partial attempt
      const { data: existingChildren } = await supabase
        .from('children')
        .select('id, name')
        .eq('parent_id', profile.id);

      if (existingChildren && existingChildren.length > 0) {
        // Resume from pricing step — child was already created in a prior attempt
        const existingChild = existingChildren[0];
        setChildren([{ id: existingChild.id, name: existingChild.name }]);
        setSelectedChildId(existingChild.id);
        setStep('pricing');
      } else {
        setStep('select_child');
      }
      
    } catch (err) {
      console.error('Account creation error:', err);
      Sentry.captureException(err, { tags: { flow: 'welcome', step: 'create_account' } });
      setError(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  const handleAddChild = () => {
    if (!newChildName.trim()) return;
    
    const newChild: ChildOption = {
      id: `new_${Date.now()}`,
      name: newChildName.trim(),
      isNew: true,
    };
    
    setChildren([...children, newChild]);
    setSelectedChildId(newChild.id);
    setNewChildName('');
    setAddingChild(false);
  };

  const handleConfirmChild = async () => {
    if (!selectedChildId || !profileId) return;
    
    const selectedChild = children.find(c => c.id === selectedChildId);
    if (!selectedChild) return;
    
    // Only use CRM birth date for the pre-populated CRM child, not for newly added children
    const isCrmChild = selectedChild.id === 'crm_child';
    const birthDate = isCrmChild && tokenData?.child_birth_year && tokenData?.child_birth_month && tokenData?.child_birth_day
      ? `${tokenData.child_birth_year}-${String(tokenData.child_birth_month).padStart(2, '0')}-${String(tokenData.child_birth_day).padStart(2, '0')}`
      : null;
    
    try {
      // Create child record in database (linked to household)
      const { data: childRecord, error: childError } = await supabase
        .from('children')
        .insert({
          parent_id: profileId,
          household_id: householdId,
          name: selectedChild.name,
          birth_date: birthDate,
          subscription_status: 'none',
        } as any)
        .select('id')
        .single();
      
      if (childError) throw childError;
      
      // Update the selected child ID to the real database ID
      setSelectedChildId(childRecord.id);
      
      // Update children array with real ID
      setChildren(children.map(c => 
        c.id === selectedChild.id ? { ...c, id: childRecord.id } : c
      ));
      
      setStep('pricing');
      
    } catch (err) {
      console.error('Child creation error:', err);
      Sentry.captureException(err, { tags: { flow: 'welcome', step: 'confirm_child' } });
      setError(err instanceof Error ? err.message : 'Failed to save child');
    }
  };

  const handleStartTrial = async () => {
    if ((!agreedToTerms && !isExistingSubscriber) || !selectedChildId || !token) return;
    
    setStartingTrial(true);
    setError(null);
    
    try {
      // Existing subscriber: skip Stripe, consume token, mark onboarding complete
      if (isExistingSubscriber) {
        if (profileId) {
          try {
            const { error: tokenError } = await (supabase.rpc as any)('use_welcome_token', {
              p_token: token,
              p_profile_id: profileId,
              p_child_id: selectedChildId,
            });
            if (tokenError) {
              console.error('use_welcome_token error:', tokenError);
              Sentry.captureException(new Error(tokenError.message), { tags: { flow: 'welcome_existing_subscriber', step: 'use_welcome_token' } });
            }
          } catch (err) {
            console.error('use_welcome_token failed:', err);
            Sentry.captureException(err, { tags: { flow: 'welcome_existing_subscriber', step: 'use_welcome_token' } });
          }
          await supabase.from('profiles').update({ onboarding_complete: true } as any).eq('id', profileId);
        }
        window.location.href = '/?onboarding=complete';
        return;
      }

      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Session expired. Please refresh and try again.');
      }
      
      const checkoutResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            childId: selectedChildId,
            priceId: packageInfo?.stripe_price_id,
            successUrl: `${window.location.origin}/?onboarding=complete`,
            cancelUrl: `${window.location.origin}/welcome/${token}?step=pricing`,
            trialDays: packageInfo?.trial_days ?? 30,
            welcomeToken: token,
          }),
        }
      );
      
      const checkoutData = await checkoutResponse.json();
      
      if (checkoutData.error) {
        throw new Error(checkoutData.error);
      }
      
      if (checkoutData.checkoutUrl) {
        if (profileId) {
          try {
            const { error: tokenErr } = await (supabase.rpc as any)('use_welcome_token', {
              p_token: token,
              p_profile_id: profileId,
              p_child_id: selectedChildId,
            });
            if (tokenErr) {
              console.error('use_welcome_token error:', tokenErr);
              Sentry.captureException(new Error(tokenErr.message), { tags: { flow: 'welcome', step: 'use_welcome_token_checkout' } });
            }
          } catch (err) {
            Sentry.captureException(err, { tags: { flow: 'welcome', step: 'use_welcome_token_checkout' } });
          }
        }
        window.location.href = checkoutData.checkoutUrl;
      } else {
        if (profileId) {
          try {
            const { error: tokenErr } = await (supabase.rpc as any)('use_welcome_token', {
              p_token: token,
              p_profile_id: profileId,
              p_child_id: selectedChildId,
            });
            if (tokenErr) {
              console.error('use_welcome_token error:', tokenErr);
              Sentry.captureException(new Error(tokenErr.message), { tags: { flow: 'welcome', step: 'use_welcome_token_success' } });
            }
          } catch (err) {
            Sentry.captureException(err, { tags: { flow: 'welcome', step: 'use_welcome_token_success' } });
          }
        }
        setStep('success');
      }
      
    } catch (err) {
      console.error('Trial start error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  // Calculate trial end date dynamically from package
  const trialTerms = generateTrialTerms(
    packageInfo?.trial_days ?? 30,
    packageInfo?.price_cents ?? 800,
    packageInfo?.billing_interval ?? 'day',
    packageInfo?.interval_count ?? 30,
  );
  const formattedTrialEnd = trialTerms.formattedTrialEnd;

  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#5C2D91] mx-auto mb-4" />
          <p className="text-[#636366]">{t('Validating your welcome link...')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center p-8 rounded-2xl" style={glassStyles.card}>
          <div className="w-16 h-16 bg-[#FF3B30]/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-[#FF3B30]" />
          </div>
          <h1 className="text-2xl font-semibold text-[#1C1C1E] mb-4">
            {t('Link Not Valid')}
          </h1>
          <p className="text-[#636366] mb-6">
            {error || t('This welcome link is invalid or has already been used.')}
          </p>
          <a
            href="/trial"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#5C2D91] text-white rounded-xl font-medium hover:bg-[#5C2D91]/80 transition-colors"
          >
            {t('Request a New Trial')}
          </a>
        </div>
      </div>
    );
  }

  // Success state
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center p-8 rounded-2xl" style={glassStyles.card}>
          <div className="w-16 h-16 bg-[#34C759]/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#34C759]" />
          </div>
          <h1 className="text-2xl font-semibold text-[#1C1C1E] mb-4">
            {t('Welcome to Turn!')}
          </h1>
          <p className="text-[#636366] mb-6">
            {t('Your account has been created and your trial is ready to begin.')}
          </p>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#5C2D91] text-white rounded-xl font-medium hover:bg-[#5C2D91]/80 transition-colors"
          >
            {t('Go to Home')}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0] py-8 px-4">
      <div className={`mx-auto ${step === 'pricing' ? 'max-w-2xl' : 'max-w-lg'}`}>
        {/* Language selector */}
        <div className="flex justify-end mb-4">
          <LanguageSelector variant="light" />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-[#1C1C1E] mb-2">
            {t("It's your turn")}
          </h1>
        </div>

        {/* Step 1: Create Account */}
        {step === 'create_account' && tokenData && (
          <div className="rounded-2xl p-6" style={glassStyles.card}>
            <div className="mb-6">
              <p className="text-[#1C1C1E] text-lg">
                {t('Hello')}, <strong className="text-[#1C1C1E]">{tokenData.contact_first_name}</strong>! 
                {tokenData.child_first_name && (
                  <> {t('A seat is now available for')} <strong className="text-[#1C1C1E]">{tokenData.child_first_name}</strong>'s {t('trial')}.</>
                )}
              </p>
              <p className="text-[#636366] mt-2">
                {t('Please create your account to get started.')}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#636366] mb-2">
                  {t('Email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#5C2D91]/30 focus:border-[#5C2D91]/50 focus:outline-none"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#636366] mb-2">
                  {t('Password')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#5C2D91]/30 focus:border-[#5C2D91]/50 focus:outline-none"
                    placeholder={t('At least 8 characters')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8E93] hover:text-[#636366]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#636366] mb-2">
                  {t('Confirm Password')}
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#5C2D91]/30 focus:border-[#5C2D91]/50 focus:outline-none"
                  placeholder={t('Confirm your password')}
                />
              </div>

              {error && (
                <div className="p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl text-[#FF3B30] text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleCreateAccount}
                disabled={creating || !email || !password || !confirmPassword}
                className="w-full px-6 py-3 bg-[#5C2D91] hover:bg-[#5C2D91]/90 disabled:bg-white/10 disabled:text-[#C7C7CC] text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('Creating Account...')}
                  </>
                ) : (
                  t('Create Account')
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Select Child (Netflix-style) */}
        {step === 'select_child' && (
          <div className="rounded-2xl p-6" style={glassStyles.card}>
            <h2 className="text-xl font-semibold text-[#1C1C1E] mb-6 text-center">
              {t('Confirm child:')}
            </h2>

            <div className="flex justify-center gap-6 mb-8">
              {children.map(child => (
                <button
                  key={child.id}
                  onClick={() => setSelectedChildId(child.id)}
                  className="flex flex-col items-center gap-3 group"
                >
                  <div
                    className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                      selectedChildId === child.id
                        ? 'bg-white'
                        : 'bg-transparent border-2 border-white/30 group-hover:border-white/50'
                    }`}
                  >
                    {selectedChildId === child.id ? (
                      <User className="w-10 h-10 text-black" />
                    ) : (
                      <User className="w-10 h-10 text-[#8E8E93]" />
                    )}
                  </div>
                  <span className={`text-sm font-medium ${
                    selectedChildId === child.id ? 'text-[#1C1C1E]' : 'text-[#636366]'
                  }`}>
                    {child.name}
                  </span>
                </button>
              ))}

              {/* Add child button */}
              <button
                onClick={() => setAddingChild(true)}
                className="flex flex-col items-center gap-3 group"
              >
                <div className="w-24 h-24 rounded-full bg-transparent border-2 border-dashed border-white/30 group-hover:border-white/50 flex items-center justify-center transition-all">
                  <Plus className="w-10 h-10 text-[#8E8E93] group-hover:text-[#636366]" />
                </div>
                <span className="text-sm font-medium text-[#636366] group-hover:text-[#1C1C1E]">
                  {t('Add child')}
                </span>
              </button>
            </div>

            {/* Add child form */}
            {addingChild && (
              <div className="mb-6 p-4 rounded-xl bg-black/[0.03] animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newChildName}
                    onChange={e => setNewChildName(e.target.value)}
                    placeholder={t("Child's first name")}
                    className="flex-1 px-4 py-2 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#5C2D91]/30 focus:border-[#5C2D91]/50 focus:outline-none"
                    autoFocus
                  />
                  <button
                    onClick={handleAddChild}
                    disabled={!newChildName.trim()}
                    className="px-4 py-2 bg-[#5C2D91] hover:bg-[#5C2D91]/90 disabled:bg-white/10 disabled:text-[#C7C7CC] text-white rounded-xl font-medium transition-colors"
                  >
                    {t('Add')}
                  </button>
                  <button
                    onClick={() => {
                      setAddingChild(false);
                      setNewChildName('');
                    }}
                    className="px-4 py-2 border border-black/[0.08] text-[#636366] hover:bg-black/[0.03] rounded-xl font-medium transition-colors"
                  >
                    {t('Cancel')}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl text-[#FF3B30] text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleConfirmChild}
              disabled={!selectedChildId}
              className="w-full px-6 py-3 bg-[#5C2D91] hover:bg-[#5C2D91]/90 disabled:bg-white/10 disabled:text-[#C7C7CC] text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {t('Continue')}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 3: Pricing & Trial */}
        {step === 'pricing' && (
          <WelcomePricingStep
            childName={children.find(c => c.id === selectedChildId)?.name ?? ''}
            formattedTrialEnd={formattedTrialEnd}
            isExistingSubscriber={isExistingSubscriber}
            setIsExistingSubscriber={setIsExistingSubscriber}
            agreedToTerms={agreedToTerms}
            setAgreedToTerms={setAgreedToTerms}
            startingTrial={startingTrial}
            handleStartTrial={handleStartTrial}
            error={error}
          />
        )}

        {/* Already have an account */}
        {step === 'create_account' && (
          <p className="text-center text-sm text-[#8E8E93] mt-6">
            {t('Already have an account?')}{' '}
            <a href="/login" className="text-[#5C2D91] hover:underline font-medium">
              {t('Log in')}
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default WelcomePage;
